import { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SpinePublisherPicker } from "./SpinePublisherPicker";
import { SpineInitDataPicker } from "./SpineInitDataPicker";
import { PreviewPicker } from "./PreviewPicker";
import useSWRMutation from "swr/mutation";
import { putFetcher } from "api/swr";
import {
  SpineTemplate,
  STATIC_SPINE_TEMPLATE_TYPES,
  isStaticSpineTemplateType,
} from "../../../../types/spine-template";
import { toast } from "@/components/ui/use-toast";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

type FormData = Pick<
  SpineTemplate,
  | "name"
  | "intro"
  | "initDataId"
  | "previewId"
  | "author"
  | "onlinePublisher"
  | "character"
  | "cover"
  | "preview"
  | "videoCover"
  | "videoPreview"
  | "videoCoverPreview"
  | "videoPreviewPreview"
  | "type"
  | "fullscreenType"
>;

const EMPTY_FORM: Partial<FormData> = {
  name: "",
  intro: "",
  initDataId: "",
  previewId: "",
  author: "",
  onlinePublisher: "",
  character: undefined,
  cover: "",
  preview: "",
  videoCover: "",
  videoPreview: "",
  videoCoverPreview: "",
  videoPreviewPreview: "",
  type: "",
  fullscreenType: 0,
};

function toFormData(template: SpineTemplate): Partial<FormData> {
  return {
    name: template.name || "",
    intro: template.intro || "",
    initDataId: template.initDataId || "",
    previewId: template.previewId || "",
    author: template.author || "",
    onlinePublisher: template.onlinePublisher || "",
    character: template.character,
    cover: template.cover || "",
    preview: template.preview || "",
    videoCover: template.videoCover || "",
    videoPreview: template.videoPreview || "",
    videoCoverPreview: template.videoCoverPreview || "",
    videoPreviewPreview: template.videoPreviewPreview || "",
    type: template.type || "",
    fullscreenType: template.fullscreenType ?? 0,
  };
}

interface EditTemplateInfoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template: SpineTemplate | undefined;
  onSaveSuccess?: () => void;
}

export function EditTemplateInfoDialog({
  open,
  onOpenChange,
  template,
  onSaveSuccess,
}: EditTemplateInfoDialogProps) {
  const { tid } = useParams<{ tid: string }>();

  const { trigger: saveTemplate, isMutating } = useSWRMutation(
    tid ? `/spine-templates/${tid}` : null,
    putFetcher,
    {
      onSuccess: () => {
        toast({
          title: "保存成功",
          description: "模板基础信息已更新",
        });
        onSaveSuccess?.();
        onOpenChange(false);
      },
      onError: (error) => {
        toast({
          variant: "destructive",
          title: "保存失败",
          description: error.message || "未知错误",
        });
      },
    },
  );

  const [formData, setFormData] = useState<Partial<FormData>>(EMPTY_FORM);
  const [previewUrl, setPreviewUrl] = useState("");
  const [originalData, setOriginalData] = useState<Partial<FormData> | null>(
    null,
  );

  useEffect(() => {
    if (open && template) {
      const data = toFormData(template);
      setFormData(data);
      setOriginalData(data);
      setPreviewUrl(template.preview || "");
    } else if (!open) {
      setFormData(EMPTY_FORM);
      setPreviewUrl("");
      setOriginalData(null);
    }
  }, [open, template]);

  const hasChanges = useMemo(() => {
    if (!originalData) return false;
    return JSON.stringify(formData) !== JSON.stringify(originalData);
  }, [formData, originalData]);

  /** 已落库的静态模版，或表单已选静态类型时，类型下拉仅展示静态子类型，避免误改成 Zip 模版 */
  const staticTypePickerOnly = useMemo(
    () =>
      isStaticSpineTemplateType(template?.type) ||
      isStaticSpineTemplateType(formData.type),
    [template?.type, formData.type],
  );

  const handleSave = async () => {
    if (!tid || !hasChanges) {
      onOpenChange(false);
      return;
    }

    await saveTemplate({
      name: formData.name || undefined,
      intro: formData.intro || undefined,
      initDataId: formData.initDataId || undefined,
      previewId:
        formData.previewId !== undefined
          ? formData.previewId
          : undefined,
      author: formData.author || undefined,
      onlinePublisher: formData.onlinePublisher || undefined,
      character:
        formData.character !== undefined ? formData.character : undefined,
      cover: formData.cover !== undefined ? formData.cover : undefined,
      preview: formData.preview || undefined,
      videoCover: formData.videoCover !== undefined ? formData.videoCover : undefined,
      videoPreview:
        formData.videoPreview !== undefined ? formData.videoPreview : undefined,
      videoCoverPreview:
        formData.videoCoverPreview !== undefined
          ? formData.videoCoverPreview
          : undefined,
      videoPreviewPreview:
        formData.videoPreviewPreview !== undefined
          ? formData.videoPreviewPreview
          : undefined,
      type: formData.type || undefined,
      fullscreenType:
        formData.fullscreenType !== undefined
          ? formData.fullscreenType
          : undefined,
    });
  };

  // 当前已选的 Cover / Preview 置顶展示（带标识），两个选择器共用
  const pinnedItems = useMemo(() => {
    const items: {
      id?: string;
      url?: string;
      videoUrl?: string;
      videoPreviewUrl?: string;
      badge: string;
    }[] = [];
    if (formData.cover) {
      items.push({
        id: formData.initDataId || undefined,
        url: formData.cover,
        videoUrl: formData.videoCover,
        videoPreviewUrl: formData.videoCoverPreview,
        badge: "当前 Cover",
      });
    }
    if (formData.preview) {
      items.push({
        id: formData.previewId || undefined,
        url: formData.preview,
        videoUrl: formData.videoPreview,
        videoPreviewUrl: formData.videoPreviewPreview,
        badge: "当前 Preview",
      });
    }
    return items;
  }, [
    formData.cover,
    formData.initDataId,
    formData.videoCover,
    formData.videoCoverPreview,
    formData.preview,
    formData.previewId,
    formData.videoPreview,
    formData.videoPreviewPreview,
  ]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>编辑模板基础信息</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* 名称 */}
          <div className="space-y-2">
            <Label>名称</Label>
            <Input
              value={formData.name || ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setFormData((prev) => ({ ...prev, name: e.target.value }))
              }
              placeholder="输入模板名称"
            />
          </div>

          {/* 描述 */}
          <div className="space-y-2">
            <Label>描述</Label>
            <Textarea
              // @ts-ignore
              value={formData.intro || ""}
              // @ts-ignore
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setFormData((prev) => ({ ...prev, intro: e.target.value }))
              }
              placeholder="输入模板描述"
              rows={3}
            />
          </div>

          {/* Painter 选择器 */}
          <SpinePublisherPicker
            value={formData.author}
            onSelect={(userId, userName) => {
              setFormData((prev) => ({
                ...prev,
                author: userId,
                onlinePublisher: userName,
              }));
            }}
            label="Painter"
            placeholder="选择 Painter"
          />

          {/* Character */}
          <div className="space-y-2">
            <Label>Character</Label>
            <Input
              type="number"
              min="1"
              step="1"
              value={formData.character ?? ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const value = e.target.value;
                if (value === "") {
                  setFormData((prev) => ({ ...prev, character: undefined }));
                } else {
                  const numValue = parseInt(value, 10);
                  if (!isNaN(numValue) && numValue > 0) {
                    setFormData((prev) => ({ ...prev, character: numValue }));
                  }
                }
              }}
              placeholder="输入大于0的数字"
            />
          </div>

          {/* 模板类型 */}
          <div className="space-y-2">
            <Label>模板类型</Label>
            <Select
              value={formData.type ?? ""}
              onValueChange={(value: string) =>
                setFormData((prev) => ({ ...prev, type: value }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="选择模板类型" />
              </SelectTrigger>
              <SelectContent>
                {staticTypePickerOnly
                  ? STATIC_SPINE_TEMPLATE_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>
                        {t}
                      </SelectItem>
                    ))
                  : [
                      <SelectItem key="character" value="character">
                        character
                      </SelectItem>,
                      <SelectItem key="bg" value="bg">
                        bg
                      </SelectItem>,
                      <SelectItem key="fg" value="fg">
                        fg
                      </SelectItem>,
                      <SelectItem key="bgEffect" value="bgEffect">
                        bgEffect
                      </SelectItem>,
                      <SelectItem key="fgEffect" value="fgEffect">
                        fgEffect
                      </SelectItem>,
                      <SelectItem key="bgStatic" value="bgStatic">
                        bgStatic
                      </SelectItem>,
                      <SelectItem key="fgStatic" value="fgStatic">
                        fgStatic
                      </SelectItem>,
                    ]}
              </SelectContent>
            </Select>
          </div>

          {/* 画幅比例 */}
          <div className="space-y-2">
            <Label>画幅比例</Label>
            <RadioGroup
              value={String(formData.fullscreenType ?? 0)}
              onValueChange={(value: string) =>
                setFormData((prev) => ({
                  ...prev,
                  fullscreenType: value === "1" ? 1 : 0,
                }))
              }
              className="flex gap-6"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="0" id="fullscreen-1-1" />
                <Label htmlFor="fullscreen-1-1" className="font-normal">
                  1:1
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="1" id="fullscreen-9-16" />
                <Label htmlFor="fullscreen-9-16" className="font-normal">
                  9:16
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* 初始数据 Avatar */}
          <SpineInitDataPicker
            value={formData.initDataId}
            coverUrl={formData.cover}
            pinnedItems={pinnedItems}
            onSelect={(avatarId, avatarUrl, videoCover, videoCoverPreview) => {
              setFormData((prev) => ({
                ...prev,
                initDataId: avatarId,
                cover: avatarUrl || "",
                videoCover: videoCover || "",
                videoCoverPreview: videoCoverPreview || "",
              }));
            }}
            label="初始数据 Avatar (Cover 的缩略图时常推荐10秒)"
            placeholder="选择初始数据 Avatar"
          />

          {tid && (
            <PreviewPicker
              value={formData.preview}
              previewUrl={previewUrl}
              pinnedItems={pinnedItems}
              onSelect={(
                previewValue,
                previewImageUrl,
                videoPreview,
                videoPreviewPreview,
                previewId,
              ) => {
                setFormData((prev) => ({
                  ...prev,
                  preview: previewValue,
                  videoPreview: videoPreview || "",
                  videoPreviewPreview: videoPreviewPreview || "",
                  previewId: previewId || "",
                }));
                setPreviewUrl(previewImageUrl);
              }}
              tid={tid}
            />
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            取消
          </Button>
          <Button onClick={handleSave} disabled={!hasChanges || isMutating}>
            {isMutating ? "保存中..." : "保存"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
